/**
 * The client-record (Ficha de Cliente) blocks on a natural person (P16b, migration 0105):
 * the children, the financial situation, the source of wealth and the expected flows.
 * Pedro 2026-10-10: the app and the form match field for field; values are entered
 * PER CATEGORY LINE and the app computes the totals; the number of children is asked
 * first and the app then opens that many lines.
 *
 * Each editor is controlled ({value, onChange}) and lives inside the Person page's Edit
 * transaction (§3g Pattern A): the page's disabled fieldset makes it read-only in Read
 * mode, and everything is sent with Save. A block with nothing in it is reported as
 * null ("not answered"), matching the server (app/schemas/person_profile.py).
 */
import {
  ActionIcon, Button, Fieldset, Grid, Group, NumberInput, Stack, Table, Text, TextInput, Tooltip,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { agreementPartiesApi } from '../api/agreementParties';
import { useAlertModal } from './AlertModalProvider';
import type {
  AmountLine, ChildEntry, ExpectedFlows, FinancialSituation, Inheritance, SourceOfWealth,
} from '../api/types';
import { apKeys, useAgreementParties } from '../hooks/useAgreementParties';

export const LINES_MAX = 12;
export const CHILDREN_MAX = 20;

// Smoke 2026-10-11 (S3, Pedro): every amount in these blocks is in THOUSANDS of euros —
// entered and shown as €'000 ("€60k" = €60,000), the same on the client-record PDF.
const K = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 });
export const eur = (n: number) => `€${K.format(n)}k`;

// ─── Blank handling ──────────────────────────────────────────────────────────

/** True when a block holds no value at all (only nulls, '' and empty lists). */
export function isBlank(v: unknown): boolean {
  if (v === null || v === undefined || v === '') return true;
  if (Array.isArray(v)) return v.every(isBlank);
  if (typeof v === 'object') return Object.values(v as object).every(isBlank);
  return false;
}

/** A block as stored: null ("not answered") when it holds no value at all. Applied on Save,
 *  never while editing (a blank line just added must stay on screen — smoke 2026-10-11, S3). */
export const blockForSave = <T,>(v: T): T | null => (isBlank(v) ? null : v);

export const emptyFinancialSituation = (): FinancialSituation => ({
  income: [], expenses: [],
  financial_assets: { deposits: null, securities: null, trusts: null, insurance: null, pension_funds: null, other: [] },
  real_assets: { main_home: null, second_home: null, art_collections: null, other_property: [], other: [] },
  liabilities: { short_term_loans: null, mortgages: null, other: [] },
});
export const emptySourceOfWealth = (): SourceOfWealth => ({
  professional_activity: null, financial_investments: null,
  business_sales: [], inheritances: [], real_estate: [], other: [],
});
export const emptyExpectedFlows = (): ExpectedFlows => ({ inflows: [], outflows: [] });

const sumLines = (lines: AmountLine[]) => lines.reduce((s, l) => s + (l.amount ?? 0), 0);
const sum = (...vals: (number | null)[]) => vals.reduce<number>((s, v) => s + (v ?? 0), 0);

/** The totals the form prints (same rule as the server's financial_totals). */
export function financialTotals(fs: FinancialSituation | null) {
  const f = fs ?? emptyFinancialSituation();
  const fa = f.financial_assets;
  const ra = f.real_assets;
  const li = f.liabilities;
  const financial = sum(fa.deposits, fa.securities, fa.trusts, fa.insurance, fa.pension_funds) + sumLines(fa.other);
  const real = sum(ra.main_home, ra.second_home, ra.art_collections) + sumLines(ra.other_property) + sumLines(ra.other);
  const liabilities = sum(li.short_term_loans, li.mortgages) + sumLines(li.other);
  return {
    income: sumLines(f.income), expenses: sumLines(f.expenses),
    financial, real, liabilities, netWorth: financial + real - liabilities,
  };
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

function Money({ label, value, onChange, hl }: {
  label?: string; value: number | null; onChange: (v: number | null) => void;
  hl?: Record<string, unknown>;
}) {
  return (
    <NumberInput
      label={label} value={value ?? ''} hideControls min={0} allowNegative={false}
      thousandSeparator="," decimalScale={2} leftSection="€k" leftSectionWidth={36}
      onChange={(v) => onChange(v === '' || v === null ? null : Number(v))}
      {...hl}
    />
  );
}

/** Description + amount lines (the form's "1–4 + a" rows). */
export function AmountLinesEditor({ title, lines, onChange, descriptionLabel = 'Description' }: {
  title: string;
  lines: AmountLine[];
  onChange: (lines: AmountLine[]) => void;
  descriptionLabel?: string;
}) {
  const set = (i: number, patch: Partial<AmountLine>) =>
    onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <Stack gap={4}>
      <Group justify="space-between">
        <Text size="sm" fw={500}>{title}</Text>
        <Text size="xs" c="dimmed">{lines.length ? `Total ${eur(sumLines(lines))}` : ''}</Text>
      </Group>
      {lines.map((l, i) => (
        <Group key={i} gap="xs" wrap="nowrap" align="flex-end">
          <TextInput style={{ flex: 2 }} placeholder={descriptionLabel} aria-label={`${title} ${i + 1}: ${descriptionLabel}`}
            value={l.description ?? ''} onChange={(e) => set(i, { description: e.currentTarget.value || null })} />
          <div style={{ flex: 1 }}>
            <Money value={l.amount} onChange={(amount) => set(i, { amount })} />
          </div>
          <Tooltip label="Remove this line" withArrow>
            <ActionIcon variant="subtle" color="red" aria-label={`Remove ${title} line ${i + 1}`}
              onClick={() => onChange(lines.filter((_, j) => j !== i))}>
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      ))}
      <Group>
        <Button size="compact-xs" variant="subtle" leftSection={<IconPlus size={12} />}
          disabled={lines.length >= LINES_MAX}
          onClick={() => onChange([...lines, { description: null, amount: null }])}>
          Add line
        </Button>
      </Group>
    </Stack>
  );
}

// ─── Financial situation (042–078) ───────────────────────────────────────────

export function FinancialSituationEditor({ value, onChange, hl }: {
  value: FinancialSituation | null;
  onChange: (v: FinancialSituation | null) => void;
  hl?: Record<string, unknown>;
}) {
  const v = value ?? emptyFinancialSituation();
  // Smoke 2026-10-11 (S3): emit as typed — collapsing a blank block to null here made a
  // freshly added (blank) line vanish. The page turns a blank block into "not answered" on Save.
  const emit = (next: FinancialSituation) => onChange(next);
  const t = financialTotals(v);
  const fa = v.financial_assets;
  const ra = v.real_assets;
  const li = v.liabilities;
  return (
    <Fieldset legend="Financial situation" {...hl}>
      <Stack>
        <Text size="xs" c="dimmed">
          Enter each line as on the client record; the totals are computed. Amounts in &apos;000 Euros.
        </Text>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <AmountLinesEditor title="Income (annual)" lines={v.income}
              onChange={(income) => emit({ ...v, income })} descriptionLabel="Source of income" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <AmountLinesEditor title="Expenses (annual)" lines={v.expenses}
              onChange={(expenses) => emit({ ...v, expenses })} descriptionLabel="Expense" />
          </Grid.Col>
        </Grid>

        <Text size="sm" fw={600}>Financial assets</Text>
        <Grid>
          {([
            ['deposits', 'Deposits'], ['securities', 'Securities'], ['trusts', 'Trusts'],
            ['insurance', 'Insurance products'], ['pension_funds', 'Pension funds'],
          ] as const).map(([k, label]) => (
            <Grid.Col key={k} span={{ base: 6, md: 4 }}>
              <Money label={label} value={fa[k]}
                onChange={(n) => emit({ ...v, financial_assets: { ...fa, [k]: n } })} />
            </Grid.Col>
          ))}
        </Grid>
        <AmountLinesEditor title="Other financial assets" lines={fa.other}
          onChange={(other) => emit({ ...v, financial_assets: { ...fa, other } })} />

        <Text size="sm" fw={600}>Real estate and other assets</Text>
        <Grid>
          {([
            ['main_home', 'Permanent home'], ['second_home', 'Second home'], ['art_collections', 'Art and collections'],
          ] as const).map(([k, label]) => (
            <Grid.Col key={k} span={{ base: 6, md: 4 }}>
              <Money label={label} value={ra[k]}
                onChange={(n) => emit({ ...v, real_assets: { ...ra, [k]: n } })} />
            </Grid.Col>
          ))}
        </Grid>
        <AmountLinesEditor title="Other property" lines={ra.other_property}
          onChange={(other_property) => emit({ ...v, real_assets: { ...ra, other_property } })} />
        <AmountLinesEditor title="Other assets" lines={ra.other}
          onChange={(other) => emit({ ...v, real_assets: { ...ra, other } })} />

        <Text size="sm" fw={600}>Liabilities</Text>
        <Grid>
          {([['short_term_loans', 'Short-term loans'], ['mortgages', 'Mortgages']] as const).map(([k, label]) => (
            <Grid.Col key={k} span={{ base: 6, md: 4 }}>
              <Money label={label} value={li[k]}
                onChange={(n) => emit({ ...v, liabilities: { ...li, [k]: n } })} />
            </Grid.Col>
          ))}
        </Grid>
        <AmountLinesEditor title="Other liabilities" lines={li.other}
          onChange={(other) => emit({ ...v, liabilities: { ...li, other } })} />

        <Table withTableBorder style={{ maxWidth: 420 }}>
          <Table.Tbody>
            {([
              ['Income', t.income], ['Expenses', t.expenses], ['Financial assets', t.financial],
              ['Real estate and other assets', t.real], ['Liabilities', t.liabilities],
            ] as const).map(([label, n]) => (
              <Table.Tr key={label}><Table.Td>{label}</Table.Td><Table.Td ta="right">{eur(n)}</Table.Td></Table.Tr>
            ))}
            <Table.Tr>
              <Table.Td fw={600}>Net worth (assets − liabilities)</Table.Td>
              <Table.Td ta="right" fw={600}>{eur(t.netWorth)}</Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Stack>
    </Fieldset>
  );
}

// ─── Source of wealth (079–090) ──────────────────────────────────────────────

function InheritancesEditor({ rows, onChange }: { rows: Inheritance[]; onChange: (r: Inheritance[]) => void }) {
  const set = (i: number, patch: Partial<Inheritance>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>Inheritances and gifts</Text>
      {rows.map((r, i) => (
        <Grid key={i} align="flex-end">
          <Grid.Col span={{ base: 6, md: 2 }}>
            <Money label="Value" value={r.amount} onChange={(amount) => set(i, { amount })} />
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 2 }}>
            <TextInput label="Date" type="date" value={r.date_received ?? ''}
              onChange={(e) => set(i, { date_received: e.currentTarget.value || null })} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 3 }}>
            <TextInput label="Donor" value={r.donor ?? ''}
              onChange={(e) => set(i, { donor: e.currentTarget.value || null })} />
          </Grid.Col>
          <Grid.Col span={{ base: 11, md: 4 }}>
            <TextInput label="Donor's source of wealth" value={r.donor_source ?? ''}
              onChange={(e) => set(i, { donor_source: e.currentTarget.value || null })} />
          </Grid.Col>
          <Grid.Col span={1}>
            <Tooltip label="Remove this line" withArrow>
              <ActionIcon variant="subtle" color="red" aria-label={`Remove inheritance ${i + 1}`}
                onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          </Grid.Col>
        </Grid>
      ))}
      <Group>
        <Button size="compact-xs" variant="subtle" leftSection={<IconPlus size={12} />}
          disabled={rows.length >= LINES_MAX}
          onClick={() => onChange([...rows, { amount: null, date_received: null, donor: null, donor_source: null }])}>
          Add line
        </Button>
      </Group>
    </Stack>
  );
}

export function SourceOfWealthEditor({ value, onChange, hl }: {
  value: SourceOfWealth | null;
  onChange: (v: SourceOfWealth | null) => void;
  hl?: Record<string, unknown>;
}) {
  const v = value ?? emptySourceOfWealth();
  const emit = (next: SourceOfWealth) => onChange(next);   // blank → null on Save (S3)
  return (
    <Fieldset legend="Source of wealth" {...hl}>
      <Stack>
        <Grid>
          <Grid.Col span={{ base: 6, md: 4 }}>
            <Money label="Professional activity (savings)" value={v.professional_activity}
              onChange={(professional_activity) => emit({ ...v, professional_activity })} />
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 4 }}>
            <Money label="Financial investments (gains)" value={v.financial_investments}
              onChange={(financial_investments) => emit({ ...v, financial_investments })} />
          </Grid.Col>
        </Grid>
        <AmountLinesEditor title="Sale of a business" lines={v.business_sales}
          onChange={(business_sales) => emit({ ...v, business_sales })} />
        <InheritancesEditor rows={v.inheritances} onChange={(inheritances) => emit({ ...v, inheritances })} />
        <AmountLinesEditor title="Real-estate deals" lines={v.real_estate}
          onChange={(real_estate) => emit({ ...v, real_estate })} />
        <AmountLinesEditor title="Other sources" lines={v.other}
          onChange={(other) => emit({ ...v, other })} />
      </Stack>
    </Fieldset>
  );
}

// ─── Expected flows (091–096) ────────────────────────────────────────────────

export function ExpectedFlowsEditor({ value, onChange, hl }: {
  value: ExpectedFlows | null;
  onChange: (v: ExpectedFlows | null) => void;
  hl?: Record<string, unknown>;
}) {
  const v = value ?? emptyExpectedFlows();
  const emit = (next: ExpectedFlows) => onChange(next);   // blank → null on Save (S3)
  return (
    <Fieldset legend="Expected flows" {...hl}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <AmountLinesEditor title="Expected inflows" lines={v.inflows}
            onChange={(inflows) => emit({ ...v, inflows })} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <AmountLinesEditor title="Expected outflows" lines={v.outflows}
            onChange={(outflows) => emit({ ...v, outflows })} />
        </Grid.Col>
      </Grid>
    </Fieldset>
  );
}

// ─── Children (016–026) ──────────────────────────────────────────────────────

/** The number of children first; the app then opens that many lines (Pedro 2026-10-10).
 *  Lowering the number below a FILLED line asks first (smoke 2026-10-11, S4); clearing the
 *  box to type another number deletes nothing until the box is left empty. */
export function ChildrenBox({ count, rows, onChange }: {
  count: number | null;
  rows: ChildEntry[];
  /** Count and lines change together, so they are reported together. */
  onChange: (count: number | null, rows: ChildEntry[]) => void;
}) {
  const { confirm } = useAlertModal();
  const n = count ?? 0;
  // The box's own text, so an empty box while retyping is not yet "no children".
  const [draft, setDraft] = useState<number | ''>(count ?? '');
  useEffect(() => { setDraft(count ?? ''); }, [count]);
  // Always show exactly `count` lines (blank ones for children not named yet).
  const lines: ChildEntry[] = Array.from({ length: n }, (_, i) => rows[i] ?? { name: null, birth_date: null });
  const set = (i: number, patch: Partial<ChildEntry>) =>
    onChange(count, lines.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  /** Apply a new count; if it drops lines that hold something, ask first. */
  async function applyCount(next: number | null) {
    const kept = next ?? 0;
    const lost = lines.slice(kept).filter((c) => !isBlank(c));
    if (lost.length > 0) {
      const names = lost.map((c, i) => c.name || `child ${kept + i + 1}`).join(', ');
      const ok = await confirm({
        title: 'Remove filled lines?',
        message: `Lowering the number of children to ${kept} removes ${lost.length === 1 ? 'a line that is' : `${lost.length} lines that are`} filled in (${names}). Remove ${lost.length === 1 ? 'it' : 'them'}?`,
        confirmLabel: 'Remove', cancelLabel: 'Keep', destructive: true,
      });
      if (!ok) { setDraft(count ?? ''); return; }
    }
    onChange(next, lines.slice(0, kept));
  }

  return (
    <Fieldset legend="Children">
      <Stack gap="xs">
        <NumberInput
          label="Number of children" min={0} max={CHILDREN_MAX} allowNegative={false} allowDecimal={false}
          style={{ maxWidth: 220 }} value={draft}
          onChange={(v) => {
            if (v === '' || v === null) { setDraft(''); return; }   // retyping: wait
            const next = Math.max(0, Math.min(CHILDREN_MAX, Number(v)));
            setDraft(next);
            void applyCount(next);
          }}
          onBlur={() => { if (draft === '' && count !== null) void applyCount(null); }}
        />
        {lines.map((c, i) => (
          <Grid key={i}>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <TextInput label={`Child ${i + 1} — full name`} value={c.name ?? ''}
                onChange={(e) => set(i, { name: e.currentTarget.value || null })} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <TextInput label="Date of birth" type="date" value={c.birth_date ?? ''}
                onChange={(e) => set(i, { birth_date: e.currentTarget.value || null })} />
            </Grid.Col>
          </Grid>
        ))}
      </Stack>
    </Fieldset>
  );
}

/** Children lines as stored: blank lines dropped, null when none. */
export function childrenForSave(rows: ChildEntry[] | null): ChildEntry[] | null {
  const kept = (rows ?? []).filter((c) => !isBlank(c));
  return kept.length ? kept : null;
}

// ─── Read-only summary (change-request diffs, §3j) ───────────────────────────

export const CLIENT_RECORD_BLOCKS = new Set([
  'children', 'financial_situation', 'source_of_wealth', 'expected_flows',
]);

const dmy = (iso: string | null | undefined) => {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};
const money = (n: number | null | undefined) => (n == null ? '' : eur(n));
const line = (l: AmountLine) => [l.description, money(l.amount)].filter(Boolean).join(' · ');

/** One block as readable lines, e.g. "Income: Salary · 60 000,00 €". Blank lines (an
 *  editor keeps them until Save) are left out, so a block reads the same with or without
 *  them (S13 compares blocks this way). */
export function describeClientBlock(field: string, value: unknown): string[] {
  if (value == null) return [];
  const out: string[] = [];
  const lines = (label: string, ls: AmountLine[] | undefined) => {
    for (const l of ls ?? []) if (!isBlank(l)) out.push(`${label}: ${line(l)}`);
  };
  const amount = (label: string, n: number | null | undefined) => {
    if (n != null) out.push(`${label}: ${money(n)}`);
  };
  if (field === 'children') {
    for (const c of value as ChildEntry[]) {
      if (!isBlank(c)) out.push([c.name, dmy(c.birth_date)].filter(Boolean).join(' · '));
    }
  } else if (field === 'expected_flows') {
    const f = value as ExpectedFlows;
    lines('Inflow', f.inflows);
    lines('Outflow', f.outflows);
  } else if (field === 'source_of_wealth') {
    const s = value as SourceOfWealth;
    amount('Professional activity', s.professional_activity);
    amount('Financial investments', s.financial_investments);
    lines('Sale of a business', s.business_sales);
    for (const i of s.inheritances ?? []) {
      if (isBlank(i)) continue;
      out.push(`Inheritance / gift: ${[money(i.amount), dmy(i.date_received), i.donor && `from ${i.donor}`,
        i.donor_source && `(${i.donor_source})`].filter(Boolean).join(' · ')}`);
    }
    lines('Real-estate deal', s.real_estate);
    lines('Other source', s.other);
  } else if (field === 'financial_situation') {
    const f = value as FinancialSituation;
    lines('Income', f.income);
    lines('Expense', f.expenses);
    const fa = f.financial_assets;
    amount('Deposits', fa?.deposits); amount('Securities', fa?.securities); amount('Trusts', fa?.trusts);
    amount('Insurance products', fa?.insurance); amount('Pension funds', fa?.pension_funds);
    lines('Other financial asset', fa?.other);
    const ra = f.real_assets;
    amount('Permanent home', ra?.main_home); amount('Second home', ra?.second_home);
    amount('Art and collections', ra?.art_collections);
    lines('Other property', ra?.other_property); lines('Other asset', ra?.other);
    const li = f.liabilities;
    amount('Short-term loans', li?.short_term_loans); amount('Mortgages', li?.mortgages);
    lines('Other liability', li?.other);
    out.push(`Net worth: ${eur(financialTotals(f).netWorth)}`);
  }
  return out;
}

// ─── Named UBOs beside the ownership structure (legal persons) ───────────────

/** Pedro 2026-10-10: the ownership structure is described in text, and "the named
 *  in-app UBOs are listed alongside it" — the beneficial-owner parties (is_ubo) on the
 *  agreements this company holds. */
export function UboList({ personId }: { personId: string }) {
  const holderRows = useAgreementParties({ person_id: personId, role: 'HOLDER', limit: 200 });
  const agreementIds = [...new Set((holderRows.data?.items ?? [])
    .filter((p) => !p.deleted_at).map((p) => p.agreement_id))];
  const parties = useQueries({
    queries: agreementIds.map((agreement_id) => ({
      queryKey: apKeys.list({ agreement_id, limit: 200 }),
      queryFn: () => agreementPartiesApi.list({ agreement_id, limit: 200 }),
    })),
  });
  const names = [...new Set(parties.flatMap((q) => (q.data?.items ?? [])
    .filter((p) => p.is_ubo && !p.deleted_at)
    .map((p) => p.person_display_name ?? '')).filter(Boolean))];
  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>Named beneficial owners</Text>
      {holderRows.isLoading || parties.some((q) => q.isLoading)
        ? <Text size="xs" c="dimmed">Loading…</Text>
        : names.length
          ? names.map((n) => <Text key={n} size="sm">{n}</Text>)
          : <Text size="xs" c="dimmed">
              None recorded. They are the beneficial-owner parties on the agreements this
              company holds (Agreement → Parties).
            </Text>}
    </Stack>
  );
}
